import { iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The low water (La Orilla 1, docs/SAGAS.md): with the sea out, one of the roads is a causeway of stones out to the
 * reef where the fishers' great net got caught. Heroes by the net work it loose (quicker with two), and one carries
 * it home to the stilt-houses at the node (slowly alone, at a walk with a friend beside). After a while the water comes
 * back: a line from the sea that eats the map along the causeway; in it the heroes wade and drown, and a net left
 * lying in it is washed away. With the water come the Drowned, who grab a hero and drag them out to sea unless
 * someone cuts them loose. That night the Drowned and the nightmares come up for the net: with it home at dawn it is
 * won; the net washed away, still out at dawn, or the node fallen loses it.
 */

/** How far out to sea a point is: along the causeway's way out (`dir`, in thousandths). */
const out = (s: BattleState, x: number, y: number): number => {
  const r = s.rules?.tide;
  return r ? idiv(x * r.dir.x + y * r.dir.y, 1000) : 0;
};

/** Whether the water has come back over a point. */
export const tideWet = (s: BattleState, x: number, y: number): boolean => !!s.tide && out(s, x, y) >= s.tide.front;

export const tideTick = (s: BattleState): void => {
  const r = s.rules?.tide;
  if (!r) return;
  const st = (s.tide ??= { front: r.start, net: { x: r.reef.x, y: r.reef.y, at: 'reef', pid: '', held: 0 }, back: 0, next: 0, done: 0, lost: 0 });
  if (!s.started || st.done || st.lost) return;
  // The water comes back from the sea, down to the shore's rocks.
  if (s.waveT >= r.low) {
    if (!st.back) {
      st.back = s.tick;
      st.next = s.tick;
      ev(s, { t: 'tide', kind: 'back', x: r.reef.x, y: r.reef.y });
    }
    st.front = imax(r.shore, st.front - r.speed);
    // The Drowned climb out at the waterline, on the causeway.
    if (s.tick >= st.next) {
      st.next = s.tick + r.drowned.every;
      const d = st.front - 400;
      spawnEnemy(s, 'drowned', 1, false, 0, idiv(r.dir.x * d, 1000), idiv(r.dir.y * d, 1000), mulBp(r.drowned.hp, s.rules?.enemyHpBp ?? 10000));
    }
  }
  // In the water: wading, and drowning.
  if (st.back && (s.tick - st.back) % 12 === 0) for (const h of s.heroes) if (h.downTicks === 0 && tideWet(s, h.x, h.y)) damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.drownBp)), null);
  const net = st.net;
  // Dawn: with the net home the night is held; without it, it is gone with the dark.
  if (s.waveT >= r.dawn) {
    if (net.at === 'home') st.done = s.tick;
    else st.lost = s.tick;
    ev(s, { t: 'tide', kind: net.at === 'home' ? 'dawn' : 'lost', x: 0, y: 0 });
    return;
  }
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  if (net.at === 'reef') {
    // Worked loose by whoever stands by it (each one's hands count).
    const by = standing.filter((h) => withinRange(h.x, h.y, net.x, net.y, r.reach)).length;
    net.held += by;
    if (net.held >= r.unhook) {
      net.at = 'ground';
      net.held = 0;
      ev(s, { t: 'tide', kind: 'loose', x: net.x, y: net.y });
    }
    return;
  }
  if (net.at === 'carried') {
    const h = s.heroes.find((x) => x.pid === net.pid);
    // A Drowned's grip doesn't make them let go: only falling does.
    if (!h || h.downTicks > 0) {
      net.at = 'ground';
      net.pid = '';
    } else {
      net.x = h.x;
      net.y = h.y;
      if (withinRange(h.x, h.y, 0, 0, r.home)) {
        net.at = 'home';
        net.pid = '';
        net.x = 0;
        net.y = 0;
        ev(s, { t: 'tide', kind: 'home', x: 0, y: 0 });
      }
    }
  }
  if (net.at === 'ground') {
    // Left lying in the water a moment: the sea takes it.
    net.soaked = tideWet(s, net.x, net.y) ? (net.soaked ?? 0) + 1 : 0;
    if (net.soaked >= r.soak) {
      st.lost = s.tick;
      ev(s, { t: 'tide', kind: 'lost', x: net.x, y: net.y });
      return;
    }
    const by = standing.find((h) => !(h.grabbed ?? 0) && withinRange(h.x, h.y, net.x, net.y, r.reach));
    if (by) {
      net.at = 'carried';
      net.pid = by.pid;
      ev(s, { t: 'tide', kind: 'lift', x: net.x, y: net.y });
    }
  }
};

/** A hero's pace: wading in the water, and carrying the net (slow alone, at a walk with another beside). */
export const tidePaceBp = (s: BattleState, h: Hero): number => {
  const r = s.rules?.tide;
  const st = s.tide;
  if (!r || !st) return 10000;
  let bp = tideWet(s, h.x, h.y) ? r.wadeBp : 10000;
  if (st.net.at === 'carried' && st.net.pid === h.pid && !s.heroes.some((o) => o !== h && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, r.pair))) bp = mulBp(bp, r.carryBp);
  return bp;
};

/** Net home: won (a moment later). Net washed away or the node fallen: lost. */
export const tideResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.tide;
  if (!r) return null;
  if (s.keep.hp <= 0 || s.tide?.lost) return 'lost';
  return s.tide?.done && s.tick - s.tide.done >= r.beat ? 'won' : null;
};

/**
 * A Drowned's grip (La Orilla's own nightmare): it takes hold of the hero it strikes and walks them out to sea, slowly,
 * until it falls, is stunned, or takes `cut` of its life in blows since it took hold. True while it holds someone.
 */
export const drownedHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.drowned;
  if (!r || !e.grab) return false;
  const h = s.heroes.find((x) => x.id === e.grab);
  if (!h || h.downTicks > 0 || e.stun > 0 || (e.cut ?? 0) >= mulBp(e.maxHp, r.cutBp)) {
    if (h) h.grabbed = 0;
    e.grab = 0;
    e.cut = 0;
    e.cd = r.again;
    if (h) ev(s, { t: 'tide', kind: 'free', x: h.x, y: h.y });
    return false;
  }
  // Out to sea, along the causeway's way out (or straight away from the node with no sea).
  const sea = s.rules?.tide?.dir ?? { x: e.x, y: e.y };
  // Out to the clearing's edge at most (past it there is only sea): there it holds them under.
  const edge = (s.map.radius - 2) * 1000;
  e.anim = 'walk';
  if (e.x * e.x + e.y * e.y < edge * edge) stepToward(e, e.x + sea.x * 4, e.y + sea.y * 4, mulBp(px, r.dragBp));
  else e.anim = 'attack';
  // The hero hangs on behind it, on the land side.
  const len = imax(1, isqrt(sea.x * sea.x + sea.y * sea.y));
  h.x = e.x - idiv(sea.x * r.hold, len);
  h.y = e.y - idiv(sea.y * r.hold, len);
  h.moving = false;
  h.path = [];
  h.facing = iatan2(e.y - h.y, e.x - h.x);
  return true;
};

/** A Drowned's blow on a hero: it takes hold (if it isn't holding anyone and the hero isn't held already). */
export const drownedGrab = (s: BattleState, e: Enemy, h: Hero): void => {
  if (e.kind !== 'drowned' || !s.rules?.drowned || e.grab || (h.grabbed ?? 0) > 0 || h.downTicks > 0) return;
  e.grab = h.id;
  e.cut = 0;
  h.grabbed = e.id;
  ev(s, { t: 'tide', kind: 'grab', x: h.x, y: h.y });
};
